import { ArrowDropDown, OpenInNew } from '@mui/icons-material';
import {
    Button,
    ButtonGroup,
    ClickAwayListener,
    FormControl,
    Grow, ListSubheader, MenuItem,
    MenuList,
    Paper,
    Popper,
    styled,
    Tooltip,
} from '@mui/material';
import {
    useEffect, useMemo, useRef, useState,
} from 'react';
import { DEFAULT_CULTURE } from 'src/constants';
import { useProductVersions } from 'src/hooks/useProductVersions';
import { config } from 'src/lib/config';
import { GroupedLocales } from 'src/components/ForcedRankingDetails/RankingTabs';
import { buildGalleryUrl } from 'src/util/galleryUrlBuilder';

interface Props {
    strategy: Models.IngestConfigApi.ForcedRankings.ForcedRankingStrategy;
    selectedVariantIndex: number;
}

const StyledPopper = styled(Popper)(() => ({
    width: 'fit-content',
    zIndex: 10001,
}));

const StyledMenuList = styled(MenuList)(({ theme }) => ({
    width: 'fit-content',
    minWidth: theme.spacing(30),
}));

const StyledButton = styled(Button)(({ theme }) => ({
    '&.Mui-disabled': {
        pointerEvents: 'auto',
    },
    '&.Mui-disabled:hover': {
        backgroundColor: theme.palette.common.white,
    },
}));

export const ViewStrategyButton = ({ strategy, selectedVariantIndex }: Props): JSX.Element => {
    const [open, setOpen] = useState(false);
    const anchorRef = useRef<HTMLDivElement>(null);
    const [selectedCulture, setSelectedCulture] = useState('');
    const { currentProductVersion } = useProductVersions(strategy.referenceProduct);
    const { groupedLocales } = config as unknown as { groupedLocales: GroupedLocales };
    const cultures = useMemo(
        () => strategy.cultureVariantRankings?.[selectedVariantIndex]?.cultures ?? [],
        [strategy, selectedVariantIndex],
    );

    useEffect(() => {
        if (cultures.includes(DEFAULT_CULTURE)) {
            setSelectedCulture(DEFAULT_CULTURE);
        } else {
            setSelectedCulture(cultures[0] ?? '');
        }
    }, [cultures]);

    const isDisabled = !strategy.name || !strategy.referenceProduct || cultures.length === 0;
    const url = buildGalleryUrl(
        selectedCulture,
        strategy.referenceProduct,
        currentProductVersion ?? undefined,
        strategy.name,
    );

    const handleClick = (): void => {
        if (!isDisabled) {
            window.open(url, '_blank')?.focus();
        }
    };

    const handleToggle = (): void => {
        setOpen((prev) => !prev);
    };

    const handleClose = (event: Event): void => {
        if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
            return;
        }

        setOpen(false);
    };

    return (
        <FormControl size="small">
            <ButtonGroup
                aria-label="View Strategy Button"
                ref={anchorRef}
                variant="outlined"
            >
                <Tooltip
                    title={
                        isDisabled
                            ? 'Add all necessary fields and save to view this strategy in a gallery.'
                            : `Select a culture to view this strategy in a gallery. Please allow up to 10 minutes after saving for the ranking to take effect.`
                    }
                >
                    <StyledButton
                        disabled={isDisabled}
                        startIcon={<OpenInNew />}
                        onClick={handleClick}
                    >
                        {isDisabled ? 'View Live Strategy' : `View Live Strategy (${selectedCulture})`}
                    </StyledButton>
                </Tooltip>
                <Tooltip title="Select culture">
                    <Button
                        disabled={isDisabled}
                        size="small"
                        onClick={handleToggle}
                    >
                        <ArrowDropDown />
                    </Button>
                </Tooltip>
            </ButtonGroup>
            <StyledPopper
                disablePortal
                transition
                anchorEl={anchorRef.current}
                open={open}
                placement="bottom-end"
            >
                {({ TransitionProps, placement }): JSX.Element => (
                    <Grow
                        {...TransitionProps}
                        style={{
                            transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
                        }}
                    >
                        <Paper>
                            <ClickAwayListener onClickAway={handleClose}>
                                <StyledMenuList id="split-button-menu">
                                    {Object.entries(groupedLocales)
                                        .filter(([, locales]) => locales.some((locale) => cultures.includes(locale)))
                                        .map(([region, locales]) => (
                                            [
                                                <ListSubheader key={region}>{region}</ListSubheader>,
                                                ...locales
                                                    .filter((locale) => cultures.includes(locale))
                                                    .flatMap((culture) => (
                                                        <MenuItem
                                                            key={culture}
                                                            selected={culture === selectedCulture}
                                                            onClick={(): void => {
                                                                setSelectedCulture(culture);
                                                                setOpen(false);
                                                            }}
                                                        >
                                                            {culture}
                                                        </MenuItem>
                                                    )),
                                            ]
                                        ))}
                                </StyledMenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </StyledPopper>
        </FormControl>
    );
};
